import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import {
  fetchActiveAssignedTests,
  mergeAbTest,
  sendAbTestAssignmentsToServer,
} from '@nextUtils/abTest/abTestUtils';
import { failure, loading, notAsked, success } from '@nextUtils/remoteData';
import { AbTests } from '@nextTypes/abTest';
import { RemoteData } from '@nextTypes/remoteData';
import { ErrorTracking } from '@common/errorTracking';

interface State {
  /**
   * The key is a unique identifier for the A/B test - this typically corresponds to the test ID
   * i.e. "442" - but it will be a string
   */
  abTests: RemoteData<string, AbTests>;
  isHydrated: boolean;
}

interface Actions {
  /**
   * Sets the A/B test assignments in the store - this should be called
   * when the app is first initialized.
   */
  setAbTests: () => Promise<void>;
  isInTestVariant: (testID: string, variant: string) => boolean;
  setHydrated: (hydrated: boolean) => void;
  /**
   * Synchronizes user A/B test assignments from the anonymous session to the user model.
   *
   * We should call this function after a user either logs in or registers.
   *
   * This is important for ensuring that the user model reflects the A/B test assignments
   * that were made during the anonymous session.
   */
  syncUserAbTests: () => Promise<void>;
}

export const useAbTestStore = create<State & Actions>()(
  persist(
    (set, get) => ({
      abTests: notAsked(),
      isHydrated: false,
      setAbTests: async () => {
        // Get active assigned tests from the server
        try {
          const { abTests } = get();

          if (abTests.type === 'NotAsked') {
            // Only set the loading state if the current state is NotAsked
            set({ abTests: loading() });
          }

          const { activeABTestAssignments } = await fetchActiveAssignedTests();

          const newAbTests: AbTests = {};
          for (const testName in activeABTestAssignments) {
            const newAssignment = activeABTestAssignments[testName];
            let existingTest = null;

            if (abTests.type === 'Success') {
              existingTest = abTests?.data?.[testName] || null;
            }
            newAbTests[testName] = mergeAbTest(existingTest, newAssignment);
          }
          // Set the new assignments in the store
          // Deliberately overwriting the existing assignments
          // This is to ensure that the store is always in sync with the server,
          // and to avoid any potential conflicts with existing assignments
          // It will also "remove" any tests that are no longer active
          // and have not been returned from the server
          set({ abTests: success(newAbTests) });
        } catch (error) {
          set({ abTests: failure(error) });
          ErrorTracking.track(error);
        }
      },
      isInTestVariant: (testID, variant) => {
        const { abTests } = get();
        if (abTests.type !== 'Success') {
          return false;
        }
        const test = abTests?.data[testID];
        return test?.variant === variant;
      },
      setHydrated: hydrated => set({ isHydrated: hydrated }),
      syncUserAbTests: async () => {
        const { abTests } = get();

        // Exit early if not in Success state
        if (abTests.type !== 'Success') {
          return;
        }

        // Find any tests that are not synced from the user model
        // i.e. they were assigned when the user was anonymous
        const unsyncedTests = Object.fromEntries(
          Object.entries(abTests.data).filter(
            ([, test]) => !test.isSyncedFromUserModel,
          ),
        );

        // If there are no unsynced tests, return
        if (Object.keys(unsyncedTests).length === 0) {
          return;
        }

        try {
          const { syncedTests } =
            await sendAbTestAssignmentsToServer(unsyncedTests);

          // Update the store to reflect that the tests have been synced
          set({
            abTests: success({ ...abTests.data, ...syncedTests }),
          });
        } catch (error) {
          ErrorTracking.track(error);
        }
      },
    }),
    {
      name: 'ab-test-storage',
      // Only persist the abTests state
      partialize: state => ({
        abTests: state.abTests,
      }),
      // Use createJSONStorage to enable SSR compatibility
      // This will use localStorage in the browser and a mock storage in SSR
      // This is important for SSR compatibility
      // and to avoid errors when accessing localStorage on the server
      // This is a workaround for Zustand's persist middleware
      storage: createJSONStorage(() =>
        typeof window !== 'undefined'
          ? localStorage
          : {
              getItem: () => null,
              setItem: () => {},
              removeItem: () => {},
            },
      ),
      // This function will be called after the stored data has been hydrated
      onRehydrateStorage: () => state => {
        if (state) {
          state.setHydrated(true);
        }
      },
    },
  ),
);
